// @flow
import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {Link, useLocation} from 'react-router-dom';
import {Divider} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import {ButtonGroup, Grid} from '@material-ui/core';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {
  HelpTwoTone,
  InfoOutlined,
  PlayCircleFilledTwoTone,
  ArrowBack,
  AccountTree,
  AssignmentInd,
  AddCircle,
} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';

import MarketingContainer from '../../marketing/containers/MarketingContainer';
import ConfigureContainer from '../../configure/containers/ConfigureContainer';
import TemplateIcon from '../../../assets/img/texTemplate.png';

import SettingIcon from '../../../assets/img/settingIcon.png';
import CommunityIcon from '../../../assets/img/communityIcon.svg';
import IdeaIcon from '../../../assets/img/ideaIcon.svg';
import ToolsIcon from '../../../assets/img/toolsIcon.svg';

import CommunityModal from '../../sidebar/components/AdditionalResources/CommunityModal';
import MyStoreContainer from '../../my-store/containers/MyStoreContainer';

import type {User, Store} from '../../../entities';
import type {Node} from 'react';
import ClientContainer from '../../client/containers/ClientContainer';
import IntegrationsContainer from '../../integrations/containers/IntegrationsContainer';
import {TIENDADA_LINKS} from '../../../constants';

type ToolsSubRoute = {
  show: boolean,
  name: string,
  subtitle?: string,
  path: string,
  component?: Node,
  icon: Node,
  disabled?: boolean,
  tierRestricted?: boolean,
  labelPro?: boolean,
  labelNuevo?: boolean,
};

type Props = {
  store: Store,
  user: User,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    background: '#f8f9fb',
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  icon: {
    height: '36px',
    width: '36px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
      marginRight: '0px',
      marginBottom: theme.spacing(2),
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  contentBottom: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    background: '#f8f9fb',
    padding: theme.spacing(0, 4),
    [theme.breakpoints.down('sm')]: {
      marginBottom: 140,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2),
    },
  },
  headerWrapper: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  ayudaButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    margin: '0 14px 0 0',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      fontSize: '12px',
      margin: '0 16px 0 0',
      padding: 0,
      boxShadow: 'unset',
    },
  },
  tutorialsButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      padding: 0,
      boxShadow: 'unset',
    },
  },
  subRouteHeader: {
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  upsellMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      // flexDirection: 'column',
      padding: theme.spacing(1),
      flexWrap: 'wrap',
    },
    color: colors.purple3,
    background: colors.lightPurple,
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  upgradeTierButton: {
    marginLeft: 'auto',
    backgroundColor: 'unset',
    color: colors.purple3,
    boxShadow: 'unset',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
  },
  toolsButton: {
    display: 'flex',
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: '20px 20px',
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
    },
  },
  disabledToolsButton: {
    display: 'flex',
    fontSize: '1.125rem',
    fontWeight: 600,
    padding: '20px 20px',
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    justifyContent: 'flex-start',
    backgroundColor: 'white',
    color: colors.grey800,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
    },
  },
  groupButton: {
    width: '100%',
    justifySelf: 'flex-end',
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '20px 20px',
    marginBottom: theme.spacing(2),
    // justifyContent: 'flex-start',
    border: 'none',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
  },
  subRouteIcon: {
    fontSize: '1.5rem !important',
    margin: '0 12px',
  },
  subRouteNameWrap: {
    height: 56,
    position: 'sticky',
    top: 72,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    backgroundColor: colors.grey200,
    // textAlign: 'left',
    padding: theme.spacing(2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  subRouteNameText: {
    fontWeight: 600,
    fontSize: '1.125rem',
    color: colors.grey900,
  },
  subRouteContent: {
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  labelButton: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      whiteSpace: 'break-spaces',
      textAlign: 'center',
    },
  },
  startIcon: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
      marginRight: '0px',
      marginBottom: theme.spacing(1),
    },
  },
  buttonLabel: {
    margin: theme.spacing(0, 2),
  },
  upsellMessageBottom: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    maxWidth: 670,
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      bottom: 64,
      left: 0,
    },
    color: colors.purple3,
    background: colors.lightPurple,
    borderRadius: '12px',
    fontSize: '0.875rem',
  },
  subRouteSubtitle: {
    fontWeight: 400,
    fontSize: '16px',
    color: colors.grey800,
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
  },
  subRouteTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  iconBack: {
    backgroundColor: 'white',
    padding: '4px',
    color: colors.grey600,
    marginRight: theme.spacing(1),
  },
  labelChip: {
    background: '#F5F0FF',
    borderRadius: '20px',
    color: colors.purple3,
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '22px',
    padding: '3px 6px',
  },
  labelChipWrap: {
    width: 'inherit',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  additionalResources: {
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: '1rem 0',
  },
  resourcesWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  resourcesText: {
    textAlign: 'center',
    color: colors.grey700,
    fontSize: '14px',
  },
}));

export default function Tools(props: Props) {
  const {store} = props;
  const classes = useStyles();
  const location = useLocation();
  const [communityModal, setCommunityModal] = useState(false);

  const subRoutes: {[key: string]: ToolsSubRoute} = {
    configure: {
      show: true,
      name: 'Configuración',
      path: '/configure',
      component: <ConfigureContainer />,
      icon: <img src={SettingIcon} alt="Reseñas" className={classes.icon} />,
      disabled: false,
      labelPro: false,
    },
    marketing: {
      show: true,
      name: 'Marketing',
      subtitle:
        'Te ayudamos a promocionar tu tienda, utiliza nuestras plantillas para promover tu tienda virtual en redes sociales. Descárgalas y compártelas en tus RRSS.',
      path: '/marketing',
      component: <MarketingContainer />,
      icon: (
        <img src={TemplateIcon} alt="Plantillas" className={classes.icon} />
      ),
      disabled: false,
      labelPro: false,
    },
    /*  virtualStore: {
      show: !!store 
      name: 'Tienda virtual',
      subtitle: '',
      path: '/my-store',
      component: <MyStoreContainer />,
      icon: (
        <img src={CustomizeIcon} alt="Mi tienda" className={classes.icon} />
      ),
      disabled: false,
      labelNuevo: !!store,
    }, */
    integrations: {
      show: true,
      name: 'Integraciones',
      subtitle: '',
      path: '/integrations',
      component: <IntegrationsContainer />,
      icon: <AccountTree className={classes.icon} />,
    },
    clients: {
      show: true,
      name: 'Clientes',
      subtitle: '',
      path: '/client',
      component: <ClientContainer />,
      icon: <AssignmentInd className={classes.icon} />,
      disabled: false,
      labelNuevo: !!store,
    },
    additionalServices: {
      show: false,
      name: 'Servicios adicionales',
      subtitle: '',
      path: '...',
      component: <MyStoreContainer />,
      icon: <AddCircle className={classes.icon} />,
      disabled: true,
      labelNuevo: !!store,
    },
  };

  const subRoutesArray = Object.keys(subRoutes).map(k => subRoutes[k]);

  const subRoute: ?ToolsSubRoute = subRoutesArray.find((route: ToolsSubRoute) =>
    location.pathname.startsWith(route.path)
  );

  const additionalResources = (
    <div className={classes.additionalResources}>
      <Grid container>
        <Grid item xs={4}>
          <div className={classes.resourcesWrapper}>
            <CustomButton
              target="_blank"
              rel="noopener noreferrer"
              href="https://tiendada.notion.site/tiendada/Recursos-TiendaDA-c8c091b522f2498aa8a4417828b48f55"
            >
              <img src={IdeaIcon} alt="additional resources" />
            </CustomButton>
            <Typography className={classes.resourcesText}>
              Recursos
              <br /> TiendaDa
            </Typography>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div className={classes.resourcesWrapper}>
            <CustomButton
              href="https://www.beacons.ai/tiendada"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={ToolsIcon} alt="additional resources" />
            </CustomButton>
            <Typography className={classes.resourcesText}>
              Herramientas
              <br /> Digitales
            </Typography>
          </div>
        </Grid>
        <Grid item xs={4}>
          <div className={classes.resourcesWrapper}>
            <CustomButton onClick={() => setCommunityModal(true)}>
              <img src={CommunityIcon} alt="additional resources" />
            </CustomButton>
            <Typography className={classes.resourcesText}>
              Únete a la
              <br /> comunidad
            </Typography>
          </div>
        </Grid>
      </Grid>
    </div>
  );
  return (
    <div className={classes.root}>
      {subRoute && (
        <Hidden mdUp>
          <div className={classes.subRouteNameWrap}>
            <CustomButton to="/marketing" className={classes.iconBack}>
              <ArrowBack />
            </CustomButton>
            <Typography component={'span'} className={classes.subRouteNameText}>
              {`Tools - ${subRoute.name}`}
            </Typography>
          </div>
          <span className={classes.subRouteSubtitle}>{subRoute.subtitle}</span>
        </Hidden>
      )}

      <main className={classes.content}>
        <Hidden smDown>
          <div className={classes.headerWrapper}>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography className={classes.title}>Tools</Typography>
              </Grid>
              <Grid item>
                <CustomButton
                  href={TIENDADA_LINKS.customerSupport.general}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.ayudaButton}
                  startIcon={<HelpTwoTone />}
                >
                  Ayuda
                </CustomButton>
                <CustomButton
                  href={TIENDADA_LINKS.tutorials.GENERAL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.tutorialsButton}
                  startIcon={<PlayCircleFilledTwoTone />}
                >
                  Tutoriales
                </CustomButton>
              </Grid>
            </Grid>
          </div>
        </Hidden>
        {subRoute && subRoute.tierRestricted && (
          <div className={classes.upsellMessage}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <InfoOutlined style={{marginRight: 16}} />
              Suscríbete a nuestros planes para acceder a funcionalidades PRO e
              impulsar tu negocio
            </div>
            <CustomButton
              href="http://wa.link/yy496f"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.upgradeTierButton}
            >
              Mejorar Plan
            </CustomButton>
          </div>
        )}

        <Grid container spacing={1}>
          {subRoutesArray.map((route: ToolsSubRoute, idx: number) =>
            !route.show ? null : (
              <Grid item key={idx} xs={6} sm={6} md={12} lg={12}>
                <CustomButton
                  // startIcon={route.icon}
                  disabled={route.disabled}
                  component={Link}
                  to={route.path}
                  classes={{
                    label: classes.labelButton,
                    // startIcon: classes.startIcon,
                  }}
                  className={`${
                    route.disabled
                      ? classes.disabledToolsButton
                      : classes.toolsButton
                  } `}
                >
                  <Hidden mdUp>
                    {route.labelPro || route.labelNuevo ? (
                      <div className={classes.labelChipWrap}>
                        <span className={classes.labelChip}>
                          {route.labelPro ? 'PRO' : 'NUEVO'}
                        </span>
                      </div>
                    ) : null}
                  </Hidden>
                  {route.icon}
                  <div className={classes.buttonLabel}>{route.name}</div>
                  <Hidden smDown>
                    {route.labelPro || route.labelNuevo ? (
                      <div className={classes.labelChipWrap}>
                        <span className={classes.labelChip}>
                          {route.labelPro ? 'PRO' : 'NUEVO'}
                        </span>
                      </div>
                    ) : null}
                  </Hidden>
                </CustomButton>
              </Grid>
            )
          )}
        </Grid>
      </main>
      <div className={classes.contentBottom}>
        <Hidden mdUp>
          <Divider className={classes.divider} />
          {additionalResources}
          <ButtonGroup>
            <CustomButton
              href={TIENDADA_LINKS.tutorials.GENERAL}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.groupButton}
              startIcon={<PlayCircleFilledTwoTone />}
            >
              Tutoriales
            </CustomButton>
            <CustomButton
              href={TIENDADA_LINKS.customerSupport.general}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.groupButton}
              startIcon={<HelpTwoTone />}
            >
              Ayuda
            </CustomButton>
          </ButtonGroup>
        </Hidden>
      </div>
      <CommunityModal
        open={communityModal}
        handleCloseModal={() => setCommunityModal(false)}
      />
    </div>
  );
}

Tools.propTypes = {
  store: PropTypes.object,
  user: PropTypes.object.isRequired,
};
